import { Controller } from "@hotwired/stimulus"
import { syncGateReason } from "../lib/gate_reason"

// Step 3 (onboarding/credentials) Continue-button gate. Disables Continue
// until both: (1) NPN field has any non-whitespace input, and (2) at
// least one licensed-state checkbox is checked. Server-side
// OnboardingStepService#save_credentials independently enforces the same
// invariant — this is the UX layer (don't let the user click Continue
// when the submit would just bounce them back).
//
// Canadian agents are exempt: OnboardingStepService#save_credentials allows
// a Canadian (User#canadian_signup?) to proceed with a blank NPN and no
// licensed states, so this gate must not block them either — the `canadian`
// value (set server-side from current_user.canadian_signup?) short-circuits
// refresh() to always-ready.
//
// Wire: data-controller="credentials-gate"
//       data-credentials-gate-canadian-value="true|false"
//       NPN input gets data-credentials-gate-target="npnInput"
//       Continue button gets data-credentials-gate-target="continueBtn"
//       (state-chip checkboxes don't need targets — their bubbling
//        change event is delegated via element-level listener.)
export default class extends Controller {
  static targets = ["continueBtn", "npnInput", "statusMessage", "stateCount"]
  static values = { canadian: Boolean }

  connect() {
    this._onChange = () => this.refresh()
    this.element.addEventListener("change", this._onChange)

    if (this.hasNpnInputTarget) {
      this._onInput = () => this.refresh()
      this.npnInputTarget.addEventListener("input", this._onInput)
    }

    this.refresh()
  }

  disconnect() {
    this.element.removeEventListener("change", this._onChange)
    if (this.hasNpnInputTarget && this._onInput) {
      this.npnInputTarget.removeEventListener("input", this._onInput)
    }
  }

  refresh() {
    const stateCount = this.selectedStateCount()
    const stateOk = stateCount > 0
    // Canadian agents are always ready — server-side save_credentials allows
    // a blank NPN and no licensed states for them, so Continue must never be
    // client-side blocked on those two fields.
    const npnOk = this.canadianValue || this.npnFilled()
    const ready = this.canadianValue || (npnOk && stateOk)

    if (this.hasContinueBtnTarget) {
      syncGateReason(this.continueBtnTarget, { ready, reason: ready ? "" : this.missingMessage(npnOk, stateOk) })
    }

    if (this.hasStatusMessageTarget) {
      if (ready) {
        this.statusMessageTarget.textContent = ""
        this.statusMessageTarget.classList.add("d-none")
      } else {
        this.statusMessageTarget.textContent = this.missingMessage(npnOk, stateOk)
        this.statusMessageTarget.classList.remove("d-none")
      }
    }

    if (this.hasStateCountTarget) {
      this.stateCountTarget.textContent = stateCount === 1 ? "1 state selected" : `${stateCount} states selected`
      this.stateCountTarget.classList.toggle("gl-onboarding-state-count--active", stateOk)
    }
  }

  // Checked chips, plus the hidden inputs the NIPR-locked layout submits in
  // their place (#14740): a locked state is selected without being a checkbox.
  selectedStateCount() {
    return this.element.querySelectorAll(
      'input[name="licensed_states[]"]:checked, input[type="hidden"][name="licensed_states[]"]'
    ).length
  }

  missingMessage(npnOk, stateOk) {
    if (!npnOk && !stateOk) return "Enter your NPN and select at least one licensed state to continue."
    if (!npnOk)             return "Enter your NPN to continue."
    return                          "Select at least one licensed state to continue."
  }

  npnFilled() {
    if (!this.hasNpnInputTarget) return false
    return this.npnInputTarget.value.trim().length > 0
  }
}
